'use client'

import { useState, useContext, useEffect, useCallback } from 'react'
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/shadcn/table'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import {
  Pencil as EditIcon,
  Eye as ViewIcon,
  Trash2 as DeleteIcon,
} from 'lucide-react'
import CreateRoleModal from '@/components/common/modals/CreateRoleModal'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import ConfirmDelete from '@/components/common/modals/ConfirmDelete'
import { SettingsContext } from '@/contexts/SettingsContext'

export default function Roles() {
  const [showCreate, setShowCreate] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [toEdit, setToEdit] = useState(null)
  const [toDelete, setToDelete] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [error, setError] = useState(null)
  const authContext = useContext(AuthContext)
  const settingsContext = useContext(SettingsContext)

  const getRoles = useCallback(async () => {
    setLoading(true)

    const res = await fetcher(
      `/api/admin/organization/${authContext.user.organization.id}/roles`,
      { method: 'GET' },
      authContext,
    )

    if (res.status === 401) {
      setLoading(false)
      return
    }

    if (res.status !== 200) {
      console.error('Failed to get organizations for user')
      setLoading(false)
      return
    }

    const data = await res.json()
    settingsContext.setRoles(data.roles || [])
    setLoading(false)
  }, [authContext, settingsContext])

  const submitRole = async (e, edit) => {
    e.preventDefault()
    setCreating(true)

    let path = '/api/admin/role'
    if (edit?.id) path += `/${edit.id}`

    const formData = new FormData(e.target)
    let obj = {}
    const isOn = v => v === 'on'
    for (const entry of formData.entries()) {
      const regex = /([a-zA-Z]+)\[([a-zA-Z]+)\]/
      const matches = entry[0].match(regex)

      if (!matches) {
        obj[entry[0]] = entry[0] === 'name' ? entry[1] : isOn(entry[1])
      } else {
        if (obj[matches[1]]) obj[matches[1]][matches[2]] = isOn(entry[1])
        else obj[matches[1]] = { [matches[2]]: isOn(entry[1]) }
      }
    }

    const res = await fetcher(
      path,
      {
        method: 'POST',
        body: JSON.stringify({
          role: {
            name: e.target.name.value,
            organization: authContext.user.organization.id,
            permissions: obj,
          },
        }),
      },
      authContext,
    )

    const data = await res.json()
    if (res.status !== 200) {
      setCreateError(data.message)
      return
    }

    if (edit?.id) {
      settingsContext.updateRole(data.role.id, data.role)
    } else {
      settingsContext.addRole(data.role)
    }
    setCreating(false)
    setCreateError(null)
    setShowCreate(false)
  }

  const deleteRole = async id => {
    settingsContext.removeRole(id)
    setToDelete(null)
    setError(null)
  }

  useEffect(() => {
    if (initialLoad) {
      getRoles()
      setInitialLoad(false)
    }
  }, [initialLoad, getRoles])

  if (!authContext.user?.isAuthorized('viewRoles')) {
    return null
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      {loading && (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {!loading && (
        <div className="grid gap-4">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold flex-1">Roles</h3>
            <ErrorAlerts error={error} />
            <Button
              onClick={() => setShowCreate(true)}
              disabled={!authContext.user?.isAuthorized('createUserGroups')}
            >
              Create Role
            </Button>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[15%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!settingsContext.roles.length && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      No roles have been created yet. Click <b>Create Role</b>{' '}
                      above to create one.
                    </TableCell>
                  </TableRow>
                )}
                {!!settingsContext.roles.length &&
                  settingsContext.roles
                    .sort((a, b) => a.name - b.name)
                    .map(ug => (
                      <TableRow key={ug.id}>
                        <TableCell>{ug.name}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={
                              authContext.user?.isAuthorized('editRoles')
                                ? 'Edit role'
                                : 'View role'
                            }
                            onClick={() => {
                              setToEdit(ug)
                              setShowCreate(true)
                            }}
                          >
                            {authContext.user?.isAuthorized('editRoles') ? (
                              <EditIcon className="size-4" />
                            ) : (
                              <ViewIcon className="size-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete role"
                            onClick={() => {
                              setToDelete(ug)
                              setShowDelete(true)
                            }}
                            disabled={
                              !authContext.user?.isAuthorized('deleteRoles')
                            }
                            className="text-destructive hover:text-destructive"
                          >
                            <DeleteIcon className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <CreateRoleModal
        open={showCreate}
        edit={toEdit}
        onClose={() => {
          setShowCreate(false)
          setToEdit(null)
        }}
        submit={submitRole}
        loading={creating}
        error={createError}
      />
      <ConfirmDelete
        open={showDelete}
        setOpen={() => setShowDelete(false)}
        title="Delete Role"
        description={
          <>
            <div>
              <p>
                Removing a role that is attached to one or more user userGroups
                will result in that role being removed from those userGroups.
              </p>
            </div>
            <div>
              <p>
                To confirm the removal of this role type its name:{' '}
                <b>{toDelete?.name}</b>
              </p>
            </div>
          </>
        }
        buttonText="Remove Role"
        fieldLabel="Role Name"
        fieldType="text"
        reference={toDelete?.name}
        apiPath={`/api/admin/role/${toDelete?.id}`}
        onComplete={() => deleteRole(toDelete?.id)}
        onCancel={() => setToDelete(null)}
      />
    </div>
  )
}
